import { createSelector } from '@reduxjs/toolkit';

import { selectAlbums } from '../../perks/store/perks.selectors';
import type { RootState } from '../../store';
import { getAlbumId, getAllArtists, getArtworkUrl } from '../models/album.helpers';
import type { Album, Track } from '../models/album.interface';

const selectMusicState = (state: RootState) => state.music;

export const selectCurrentMusicAlbumId = createSelector(selectMusicState, (s) => s.currentAlbumId);
export const selectCurrentTrackIndex = createSelector(selectMusicState, (s) => s.currentTrackIndex);
export const selectMusicIsPlaying = createSelector(selectMusicState, (s) => s.isPlaying);
export const selectMusicHasActivated = createSelector(selectMusicState, (s) => s.hasActivated);
export const selectMusicIsShuffling = createSelector(selectMusicState, (s) => s.isShuffle);
export const selectMusicSeekToStartAt = createSelector(selectMusicState, (s) => s.seekToStartAt);
export const selectQueueSource = createSelector(selectMusicState, (s) => s.queueSource);
export const selectPostQueue = createSelector(selectMusicState, (s) => s.postQueue);
export const selectClientIp = createSelector(selectMusicState, (s) => s.clientIp);
export const selectMusicAlbum = (albumId: string) =>
  createSelector(selectMusicState, (s) => s.albums[albumId] ?? null);

export const selectMusicAlbumLoading = (albumId: string) =>
  createSelector(selectMusicState, (s) => s.albumsLoading[albumId] ?? false);

export const selectMusicAlbumError = (albumId: string) =>
  createSelector(selectMusicState, (s) => s.albumsError[albumId] ?? null);

export const selectMusicAlbumNotFound = (albumId: string) =>
  createSelector(selectMusicState, (s) => s.albumsNotFound[albumId] ?? false);

export const selectMusicAlbumAccess = (albumId: string) =>
  createSelector(selectMusicState, (s) => s.albumsAccess[albumId] ?? null);

export const selectCurrentMusicAlbum = createSelector(
  selectAlbums,
  selectCurrentMusicAlbumId,
  (state: RootState) => state.music.albums,
  (perksAlbums, albumId, musicAlbums): Album | null => {
    // Prefer music-store album: it has fresh tokens after loadAlbum.fulfilled
    if (albumId && musicAlbums[albumId]) return musicAlbums[albumId];
    if (!perksAlbums?.length) return null;
    return perksAlbums.find((a: Album) => getAlbumId(a) === albumId) ?? perksAlbums[0] ?? null;
  }
);

export const selectPlayerTracks = createSelector(
  selectCurrentMusicAlbum,
  selectQueueSource,
  selectPostQueue,
  (album, queueSource, postQueue): Track[] => {
    // A post-audio play makes the post queue the live queue; album derivation
    // (including its first-album fallback) applies only in album mode.
    if (queueSource === 'post') return postQueue;
    if (!album) return [];
    const poster = getArtworkUrl(album.images);
    return [...album.tracks]
      .sort((a, b) => a.order - b.order)
      .map((track: Track) => ({
        ...track,
        albumId: getAlbumId(album),
        albumTitle: album.title,
        mergedArtists: getAllArtists(track.parties)
          .map((a) => a.name)
          .join(', '),
        poster,
      }));
  }
);
